import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ResponsiveContainer, ResponsiveContent, ResponsiveHeader, ResponsiveFooter, Card, Button } from '../ui';
import { useSessionPersistence } from '../../hooks/useSessionPersistence';

interface ExecutorInvitation {
  id: string;
  name: string;
  email: string;
  relationship: string;
  role: 'primary' | 'secondary';
  status: 'pending' | 'accepted' | 'declined';
  invitedAt: string;
}

const Frame165: React.FC = () => {
  const navigate = useNavigate();
  const { session, completeTask } = useSessionPersistence();
  const [executors, setExecutors] = useState<ExecutorInvitation[]>([]);

  useEffect(() => {
    // Load executor data from session
    const executorData = session.taskProgress?.executors?.answers || {};
    console.log('Frame165: Loading executor data:', executorData);
    
    if (executorData.selectedExecutors && executorData.invitedExecutors) {
      const selectedIds = executorData.selectedExecutors;
      const allExecutors = executorData.invitedExecutors;
      
      // Filter to get only selected executors
      const selectedExecutors = allExecutors.filter((executor: unknown) => 
        selectedIds.includes(executor.id)
      );
      
      console.log('Frame165: Selected executors:', selectedExecutors);
      setExecutors(selectedExecutors);
    } else {
      console.log('Frame165: No executor data found');
      setExecutors([]);
    }
  }, [session]);

  const handleEdit = () => {
    navigate('/frame-158');
  };

  const handleComplete = async () => {
    await completeTask('executors');
    await completeTask('legal_essentials');  // Complete parent Legal Essentials task
    navigate('/frame-140');
  };

  const getStatusBadge = (status: string) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      declined: 'bg-red-100 text-red-800'
    };
    return styles[status as keyof typeof styles] || styles.pending;
  };

  const pendingInvitations = executors.filter(e => e.status === 'pending').length;
  const acceptedInvitations = executors.filter(e => e.status === 'accepted').length;

  return (
    <ResponsiveContainer>
      <ResponsiveContent>
        <ResponsiveHeader
          title={`Here's your executor team, ${session.name || 'there'}!`}
        />

        <div className="space-y-6">
          {/* Summary Stats */}
          <Card>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold text-cyan-900">{executors.length}</div>
                <div className="text-sm text-stone-600">Total Executors</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{acceptedInvitations}</div>
                <div className="text-sm text-stone-600">Accepted</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-yellow-600">{pendingInvitations}</div>
                <div className="text-sm text-stone-600">Pending</div>
              </div>
            </div>
          </Card>

          {/* Executor List */}
          <Card>
            <div className="flex justify-between items-start mb-4">
              <h3 className="text-lg font-semibold text-stone-800">Selected Executors</h3>
              <Button 
                variant="outline" 
                size="sm" 
                onClick={handleEdit}
              >
                Edit
              </Button>
            </div>
            
            {executors.length > 0 ? (
              <div className="space-y-4">
                {executors.map((executor, index) => (
                  <div key={executor.id || index} className="border border-stone-200 rounded-lg p-4">
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <h4 className="font-medium text-stone-800">{executor.name}</h4>
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${executor.role === 'primary' ? 'bg-cyan-100 text-cyan-800' : 'bg-stone-100 text-stone-600'}`}>
                            {executor.role === 'primary' ? 'Primary' : 'Secondary'}
                          </span>
                        </div>
                        <p className="text-sm text-stone-600">{executor.email}</p>
                        <p className="text-sm text-stone-600">{executor.relationship}</p>
                      </div>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${getStatusBadge(executor.status)}`}>
                        {executor.status.charAt(0).toUpperCase() + executor.status.slice(1)}
                      </span>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center py-8 text-stone-500">
                <p>No executors selected yet.</p>
                <Button variant="outline" onClick={handleEdit} className="mt-2">
                  Select Executors
                </Button>
              </div>
            )}
          </Card>

          {/* Next Steps */}
          {pendingInvitations > 0 && (
            <Card className="bg-yellow-50 border-yellow-200">
              <div className="flex items-start gap-3">
                <div className="w-5 h-5 rounded-full bg-yellow-400 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-medium text-yellow-800">Pending Invitations</h4>
                  <p className="text-sm text-yellow-700 mt-1">
                    You have {pendingInvitations} pending executor invitation{pendingInvitations !== 1 ? 's' : ''}. 
                    Your executors will receive email invitations to review and accept their role.
                  </p>
                </div>
              </div>
            </Card>
          )}
        </div>
      </ResponsiveContent>

      <ResponsiveFooter
        title="Executors"
        subtitle="Your trusted team to manage your estate"
        progressPercent={33}
        onNext={handleComplete}
        onBack={() => navigate('/frame-163')}
        nextText="Continue to Dashboard →"
      />
    </ResponsiveContainer>
  );
};

export default Frame165;